import { useMemo } from 'react';
import { RefreshCw, Activity, Clock } from 'lucide-react';
import WorkersTable from '@/components/workers/WorkersTable';
import StatsCard from '@/components/common/StatsCard';
import { useWorkersEnriched } from '@/hooks/useJobs';

export default function WorkersPage() {
  const { data: workersData, isLoading, refetch } = useWorkersEnriched({
    sort_by: 'last_heartbeat_ts:desc',
    limit: 500,
  });

  // Sort workers by status priority, then by last heartbeat
  const workers = useMemo(() => {
    if (!workersData?.items) return [];

    return [...workersData.items].sort((a, b) => {
      // Sort by status priority (processing > idle > inactive), then by last heartbeat
      const statusOrder = { processing: 0, idle: 1, inactive: 2 };
      if (statusOrder[a.status] !== statusOrder[b.status]) {
        return statusOrder[a.status] - statusOrder[b.status];
      }
      return new Date(b.last_heartbeat_ts).getTime() - new Date(a.last_heartbeat_ts).getTime();
    });
  }, [workersData]);

  const stats = useMemo(() => {
    return {
      total: workers.length,
      active: workers.filter((w) => w.status === 'processing').length,
      idle: workers.filter((w) => w.status === 'idle').length,
      inactive: workers.filter((w) => w.status === 'inactive').length,
    };
  }, [workers]);

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Workers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Active workers registered with the orchestrator
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          label="Total Workers"
          value={stats.total.toString()}
          icon={Activity}
          variant="default"
        />
        <StatsCard
          label="Active"
          value={stats.active.toString()}
          icon={Activity}
          variant="success"
        />
        <StatsCard
          label="Idle"
          value={stats.idle.toString()}
          icon={Clock}
          variant="primary"
        />
        <StatsCard
          label="Inactive"
          value={stats.inactive.toString()}
          icon={Clock}
          variant="warning"
        />
      </div>

      {/* Workers Table */}
      <div className="glass-card rounded-lg overflow-hidden">
        <WorkersTable workers={workers} loading={isLoading} />
      </div>
    </div>
  );
}
